// Settings (route #/settings, DESIGN §3 + §8): deliberately tiny — Account (signed in: who you are, Change password,
// Sign out) · Workspace data (admins only, while sample data exists) · About. The team is managed on People.
import './settings.css';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { PASSWORD_MAX, PASSWORD_MIN, plural } from '../../shared/constants.js';
import { applyAction } from '../../shared/core.js';
import { MODE, accountOf, canManageTeam, changePassword, dispatch, logout, useStore } from '../store.js';
import { Page } from '../components/page.jsx';
import { Panel } from '../components/panel.jsx';
import { Button, Field, TextInput, toast } from '../components/ui.jsx';
import { SEARCH_SHORTCUT } from '../components/search.jsx';

const APP_VERSION = 'FRAZIER 2.1';

function joinList(parts) {
  if (parts.length < 2) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

// "7 clients, 212 batches, 60 events and 6 team members" (months are implied by clients).
function describeCounts(c = {}) {
  return joinList([
    c.clients && plural(c.clients, 'client'),
    c.batches && plural(c.batches, 'batch', 'batches'),
    c.events && plural(c.events, 'event'),
    c.team && plural(c.team, 'team member'),
  ].filter(Boolean));
}

// What "Remove sample data" would do, computed by running the (pure) reducer on the current state, so the prompt
// states exactly what goes and what stays (QA-11). null when there is no sample data.
function samplePreview(state) {
  const has = ['clients', 'months', 'batches', 'events', 'team', 'activity'].some((k) => (state[k] || []).some((e) => e.sample));
  if (!has) return null;
  try {
    return applyAction(state, { type: 'sample.clear' }).result;
  } catch {
    return { removed: {}, kept: {} };
  }
}

// Two-step inline confirm that stays armed until answered; Cancel gives focus back to the trigger.
function ConfirmRemove({ label, prompt, onConfirm }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef(null);
  const triggerRef = useRef(null);
  const wasArmed = useRef(false);
  useEffect(() => {
    if (armed) confirmRef.current?.focus();
    else if (wasArmed.current) triggerRef.current?.focus();
    wasArmed.current = armed;
  }, [armed]);

  if (!armed) {
    return (
      <button ref={triggerRef} type="button" class="btn btn-secondary settings-danger" onClick={() => setArmed(true)}>
        {label}
      </button>
    );
  }
  const run = () => {
    setBusy(true);
    Promise.resolve(onConfirm()).catch(() => {}).finally(() => { setBusy(false); setArmed(false); });
  };
  const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    setArmed(false);
  };
  return (
    <div class="settings-confirm" role="group" aria-label={label} onKeyDown={onKeyDown}>
      <p class="settings-confirm-text" role="alert">{prompt}</p>
      <div class="settings-confirm-actions">
        <Button variant="ghost" onClick={() => setArmed(false)}>Cancel</Button>
        <button ref={confirmRef} type="button" class="btn btn-danger" disabled={busy} onClick={run}>
          {busy ? 'Removing…' : label}
        </button>
      </div>
    </div>
  );
}

// Settings → Account → Change password (DESIGN §8): current + new + confirm, at least PASSWORD_MIN characters.
// A field's error shows once it has been left (or on submit) and clears as soon as the value is fixed.
const PW_FIELDS = ['current', 'next', 'confirm'];

function passwordErrors(x) {
  const e = {};
  if (!x.current) e.current = 'Enter your current password.';
  if (!x.next) e.next = 'Enter a new password.';
  else if (x.next.length < PASSWORD_MIN) e.next = `Use at least ${PASSWORD_MIN} characters.`;
  else if (x.next.length > PASSWORD_MAX) e.next = `Use at most ${PASSWORD_MAX} characters.`;
  else if (x.current && x.next === x.current) e.next = 'Choose a password different from the current one.';
  if (!x.confirm) e.confirm = 'Type the new password again.';
  else if (x.confirm !== x.next) e.confirm = 'The passwords don’t match.';
  return e;
}

function ChangePassword({ loginId, temporary }) {
  const empty = { current: '', next: '', confirm: '' };
  const [values, setValues] = useState(empty);
  const [shown, setShown] = useState({});
  const [serverError, setServerError] = useState({});
  const [busy, setBusy] = useState(false);
  const formRef = useRef(null);
  const live = passwordErrors(values);
  // Opened right after signing in with a temporary password: bring the form into view, ready to type.
  useEffect(() => {
    if (!temporary) return;
    formRef.current?.scrollIntoView({ block: 'center' });
    formRef.current?.querySelector('[name="current"]')?.focus({ preventScroll: true });
  }, []);
  const errorOf = (k) => serverError[k] || (shown[k] ? live[k] : '');

  const bind = (k, autoComplete) => ({
    name: k,
    type: 'password',
    autoComplete,
    value: values[k],
    invalid: !!errorOf(k),
    onValue: (v) => { setValues((prev) => ({ ...prev, [k]: v })); setServerError({}); },
    onBlur: () => { if (values[k]) setShown((prev) => ({ ...prev, [k]: true })); },
  });

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    const first = PW_FIELDS.find((k) => live[k]);
    if (first) {
      setShown({ current: true, next: true, confirm: true });
      formRef.current?.querySelector(`[name="${first}"]`)?.focus();
      return;
    }
    setBusy(true);
    changePassword(values.current, values.next)
      .then(() => {
        toast('Password changed.', { tone: 'success' });
        setValues(empty);
        setShown({});
      })
      .catch((err) => {
        const message = err.message || 'Could not change the password. Try again.';
        const key = /current/i.test(message) ? 'current' : 'form';
        setServerError({ [key]: message });
        if (key === 'current') formRef.current?.querySelector('[name="current"]')?.select();
      })
      .finally(() => setBusy(false));
  };

  return (
    <form ref={formRef} class="settings-password" onSubmit={submit} noValidate aria-labelledby="settings-pw-title">
      <h3 class="settings-subtitle" id="settings-pw-title">Change password</h3>
      {temporary && <p class="settings-temp" role="status">You’re using a temporary password. Choose your own — the current password is the one you signed in with.</p>}
      <input type="text" name="username" autoComplete="username" value={loginId} readOnly hidden />
      <Field label="Current password" error={errorOf('current')}><TextInput {...bind('current', 'current-password')} /></Field>
      <Field label="New password" error={errorOf('next')} hint={`At least ${PASSWORD_MIN} characters.`}><TextInput {...bind('next', 'new-password')} /></Field>
      <Field label="Confirm new password" error={errorOf('confirm')}><TextInput {...bind('confirm', 'new-password')} /></Field>
      {serverError.form && <p class="field-error" role="alert">{serverError.form}</p>}
      <div><Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : 'Change password'}</Button></div>
    </form>
  );
}

// Who you are (read-only — an admin changes role and login ID on People), Change password, Sign out.
function AccountPanel({ me }) {
  const rows = [['Name', me.name], ['Login ID', me.loginId, 'login-id'], ['Role', me.role || 'Team member'], ['Department', me.department]];
  return (
    <Panel title="Account" flush actions={<Button size="sm" variant="ghost" icon="logout" onClick={() => logout()}>Sign out</Button>}>
      <dl class="settings-about">
        {rows.map(([label, value, cls]) => (
          <div class="settings-about-row" key={label}>
            <dt>{label}</dt>
            <dd class={cls}>{value || '—'}</dd>
          </div>
        ))}
      </dl>
      <ChangePassword loginId={me.loginId || ''} temporary={me.temporaryPassword === true} />
    </Panel>
  );
}

export function SettingsView() {
  const snap = useStore();
  const { state } = snap;
  const account = accountOf(snap);
  const admin = canManageTeam(snap);
  const preview = useMemo(() => (state && admin ? samplePreview(state) : null), [state, admin]);
  const statusRef = useRef(null);
  if (!state) return null;

  const removing = describeCounts(preview?.removed);
  const kept = preview?.kept?.clients || 0;

  const removeSample = () => dispatch('sample.clear').then((result) => {
    const text = describeCounts(result?.removed);
    const off = result?.kept?.team || 0; // sample members your work still names: kept for history, switched off
    const keptNote = off ? ` ${plural(off, 'sample member')} named in your work ${off === 1 ? 'was' : 'were'} kept and switched off.` : '';
    toast(`${text ? `Sample data removed: ${text}.` : 'Sample data removed.'}${keptNote}`);
    // The confirm button is gone now: keep keyboard focus on the section.
    setTimeout(() => statusRef.current?.focus(), 0);
  });

  const prompt = [
    removing ? `Removes ${removing}.` : 'Removes every sample record.',
    kept ? `Keeps ${plural(kept, 'sample client')} that ${kept === 1 ? 'holds' : 'hold'} your own work.` : 'Your own work stays.',
    'This can’t be undone.',
  ].join(' ');

  return (
    <Page title="Settings" class="settings">
      {account && <AccountPanel me={account} />}

      {admin && (
        <Panel title="Workspace data">
          <div class="settings-row">
            <p class="settings-text" ref={statusRef} tabIndex={-1}>
              {preview
                ? 'Sample data is loaded so you can explore FRAZIER.'
                : 'No sample data. Everything here is your team’s work.'}
            </p>
            {preview && <ConfirmRemove label="Remove sample data" prompt={prompt} onConfirm={removeSample} />}
          </div>
        </Panel>
      )}

      <Panel title="About" flush>
        <dl class="settings-about">
          <div class="settings-about-row">
            <dt>Version</dt>
            <dd>{APP_VERSION}</dd>
          </div>
          <div class="settings-about-row">
            <dt>Storage</dt>
            <dd>{MODE === 'local' ? 'This browser only' : 'Saved on the server'}</dd>
          </div>
          <div class="settings-about-row settings-shortcut">
            <dt>Search</dt>
            <dd><span class="kbd">{SEARCH_SHORTCUT}</span></dd>
          </div>
        </dl>
      </Panel>
    </Page>
  );
}
